import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import debounce from 'lodash/debounce';
import { Input, Button, Icon } from 'antd';
import PropTypes from 'prop-types';
import { checkUserIdentifierAvailabilityAPI } from './api';
import s from './UserDetail.module.scss';

const normalizeDraft = value => (value || '').trim().toLowerCase();

const ClusterIdentifierEditor = ({
  userId,
  clusterId,
  currentIdentifier,
  onRequestSave,
}) => {
  const [draft, setDraft] = useState(currentIdentifier || '');
  const [checkStatus, setCheckStatus] = useState('idle');
  const [liveMessage, setLiveMessage] = useState('');
  const latestRequestValueRef = useRef(null);
  const draftRef = useRef(currentIdentifier || '');

  const runAvailabilityCheck = useCallback(
    async normalizedValue => {
      latestRequestValueRef.current = normalizedValue;
      setCheckStatus('loading');
      setLiveMessage('Checking identifier availability');

      try {
        const { data } = await checkUserIdentifierAvailabilityAPI({
          userId,
          clusterId,
          identifier: normalizedValue,
        });

        if (latestRequestValueRef.current !== normalizedValue) {
          return;
        }
        if (normalizeDraft(draftRef.current) !== normalizedValue) {
          return;
        }

        if (data.available) {
          setCheckStatus('available');
          setLiveMessage('Identifier available');
        } else {
          setCheckStatus('unavailable');
          setLiveMessage('Identifier unavailable');
        }
      } catch (e) {
        if (latestRequestValueRef.current !== normalizedValue) {
          return;
        }
        setCheckStatus('unavailable');
        setLiveMessage('Identifier unavailable');
      }
    },
    [clusterId, userId]
  );

  const debouncedCheck = useMemo(
    () =>
      debounce(normalizedValue => {
        runAvailabilityCheck(normalizedValue);
      }, 2000),
    [runAvailabilityCheck]
  );

  useEffect(() => () => debouncedCheck.cancel(), [debouncedCheck]);

  useEffect(() => {
    draftRef.current = currentIdentifier || '';
    setDraft(currentIdentifier || '');
    setCheckStatus('idle');
    setLiveMessage('');
    latestRequestValueRef.current = null;
    debouncedCheck.cancel();
  }, [currentIdentifier, userId, clusterId, debouncedCheck]);

  const handleDraftChange = e => {
    const { value } = e.target;
    draftRef.current = value;
    setDraft(value);
    debouncedCheck.cancel();

    const normalized = normalizeDraft(value);
    const currentNormalized = normalizeDraft(currentIdentifier);

    if (!normalized || normalized === currentNormalized) {
      setCheckStatus('idle');
      setLiveMessage('');
      latestRequestValueRef.current = null;
      return;
    }

    setCheckStatus('loading');
    setLiveMessage('Checking identifier availability');
    debouncedCheck(normalized);
  };

  const normalizedDraft = normalizeDraft(draft);
  const normalizedCurrent = normalizeDraft(currentIdentifier);
  const canSave =
    checkStatus === 'available' &&
    normalizedDraft &&
    normalizedDraft !== normalizedCurrent;

  const statusIcon = useMemo(() => {
    if (checkStatus === 'loading') {
      return (
        <Icon
          type="loading"
          className={s['user-detail__identifier-status']}
          aria-label="Checking identifier"
        />
      );
    }
    if (checkStatus === 'available') {
      return (
        <Icon
          type="check-circle"
          theme="twoTone"
          twoToneColor="#52c41a"
          className={s['user-detail__identifier-status']}
          aria-label="Identifier available"
        />
      );
    }
    if (checkStatus === 'unavailable') {
      return (
        <Icon
          type="close-circle"
          theme="twoTone"
          twoToneColor="#ff4d4f"
          className={s['user-detail__identifier-status']}
          aria-label="Identifier unavailable"
        />
      );
    }
    return null;
  }, [checkStatus]);

  const handleSaveClick = () => {
    if (!canSave) {
      return;
    }
    onRequestSave({
      userId,
      clusterId,
      identifier: normalizedDraft,
      currentIdentifier: currentIdentifier || '',
    });
  };

  return (
    <div className={s['user-detail__identifier-row']}>
      <Input
        value={draft}
        onChange={handleDraftChange}
        className={s['user-detail__identifier-input']}
        aria-label="User identifier"
      />
      {statusIcon}
      <Button
        type="primary"
        size="small"
        disabled={!canSave}
        onClick={handleSaveClick}
        aria-label="Save user identifier"
      >
        Save
      </Button>
      <span className={s['user-detail__identifier-live']} aria-live="polite">
        {liveMessage}
      </span>
    </div>
  );
};

ClusterIdentifierEditor.propTypes = {
  userId: PropTypes.number.isRequired,
  clusterId: PropTypes.number.isRequired,
  currentIdentifier: PropTypes.string,
  onRequestSave: PropTypes.func.isRequired,
};

ClusterIdentifierEditor.defaultProps = {
  currentIdentifier: '',
};

export default ClusterIdentifierEditor;
